import type { ApiClient } from '@commander/api-client';
import { useAuth } from '@commander/auth';
import type { CompanyEmployee } from '@commander/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect } from 'react';

/**
 * User details type based on the API response
 */
export interface UserDetails {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  photo_url: string | null;
  timezone: string;
  country_code: string;
  phone_number: string | null;
  onboarding_complete?: boolean;
  crm_joyride_viewed: boolean;
  joyride_viewed: boolean;
  step_number: number;
  email_signature?: string | null;
  company: {
    id: string;
    name: string;
    domain_name: string;
    logo_url: string | null;
    cro_admin_user_id: string;
    quote_subtitle: string | null;
    allow_reps_to_reassign_owners: boolean;
    /** 'full' for a normal account, 'trial' for a server-restricted trial org. */
    account_type?: 'full' | 'trial';
    /** When the trial expires (ISO). Null for non-trials. */
    trial_end_ts?: string | null;
    /** Whether CRM deep research / enrichment is enabled for this company. */
    crm_deep_research_enabled?: boolean;
    /** Agreement type new services inherit when they set none of their own. */
    default_service_agreement_type?: string;
  } | null;
  user_setting?: {
    new_feature_tutorial?: boolean;
    outgoing_email_recipients?: {
      to: string[];
      cc: string[];
      bcc: string[];
    } | null;
    communication_style_text?: string;
    communication_style?: string;
    appearance?: string;
    phone_verified?: boolean;
    auto_pilot_enabled?: boolean;
    contacts_notification?: boolean;
    next_step_notification?: boolean;
    status_notification?: boolean;
    intel_notification?: boolean;
    prospects_notification?: boolean;
  };
  auto_pilot_enabled: boolean;
  /** Standing "trigger on first send" setting (admin-managed); when true, first emails to
   * prospects auto-enroll into follow-up sequences without the per-send Compose toggle. */
  autopilot_first_send_enabled?: boolean;
  phone_verified: boolean;
  appearance: 'light' | 'dark' | 'system';
  communication_style: string;
  communication_style_text: string;
  outgoing_email_recipients: {
    to: string[];
    cc: string[];
    bcc: string[];
  } | null;
  new_feature_tutorial: boolean;
  data_generated: boolean;
  social_integrations: Record<string, boolean>;
  company_employee?: CompanyEmployee | null;
}

/**
 * Context value type for user details
 */
interface UserDetailsContextValue {
  userDetails: UserDetails | null | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Resolves once the refetch has settled, so callers that must act on fresh
   * data (e.g. onboarding completion) can await it before navigating. */
  refetch: () => Promise<unknown>;
}

/**
 * React Context for user details
 */
const UserDetailsContext = createContext<UserDetailsContextValue | null>(null);

/**
 * Props for UserDetailsProvider
 */
interface UserDetailsProviderProps {
  children: ReactNode;
  apiClient: ApiClient;
}

type AuthRelevantCompanyEmployee = Pick<
  CompanyEmployee,
  'id' | 'company_id' | 'user_id' | 'role' | 'status' | 'app_access'
>;

function sortedAppAccess(appAccess: CompanyEmployee['app_access']): string[] {
  return [...(appAccess ?? [])].sort();
}

function authCompanyEmployeeChanged(
  currentEmployee: CompanyEmployee | null | undefined,
  freshEmployee: CompanyEmployee | null | undefined
): boolean {
  if (!currentEmployee && !freshEmployee) return false;
  if (!currentEmployee || !freshEmployee) return true;

  const fields: Array<keyof AuthRelevantCompanyEmployee> = [
    'id',
    'company_id',
    'user_id',
    'role',
    'status',
  ];

  const scalarFieldChanged = fields.some(
    (field) => currentEmployee[field] !== freshEmployee[field]
  );
  if (scalarFieldChanged) return true;

  const currentAppAccess = sortedAppAccess(currentEmployee.app_access);
  const freshAppAccess = sortedAppAccess(freshEmployee.app_access);

  return (
    currentAppAccess.length !== freshAppAccess.length ||
    currentAppAccess.some((app, index) => app !== freshAppAccess[index])
  );
}

/**
 * Provider component that manages user details via backend API
 *
 * Features:
 * - Fetches complete user details from backend on mount
 * - Automatically refetches on window focus
 * - Automatically refetches on route change
 * - Only fetches when user is authenticated
 * - Provides cached user details to all components
 *
 * @example
 * ```tsx
 * <UserDetailsProvider apiClient={apiClient}>
 *   <App />
 * </UserDetailsProvider>
 * ```
 */
export function UserDetailsProvider({ children, apiClient }: UserDetailsProviderProps) {
  const { isAuthenticated, user: authUser, updateUser } = useAuth();

  // Fetch user details from backend - ONLY when authenticated
  const {
    data: userDetails,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    // Static key — shared across all routes so React Query reuses the cache
    // instead of creating a new entry (and firing a new request) on every navigation.
    queryKey: ['user-details'],
    queryFn: async () => {
      try {
        const response = await apiClient.get<{ data: UserDetails; success: boolean }>(
          '/users/details'
        );
        return response.data;
      } catch (error) {
        console.error('Failed to fetch user details:', error);
        // Return null if fetch fails - components should handle this gracefully
        return null;
      }
    },
    enabled: isAuthenticated, // Only fetch when user is logged in
    staleTime: 5 * 60 * 1000, // 5 minutes — user profile data changes infrequently
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: false, // User details don't change on focus
    refetchOnMount: false, // Single fetch per session; manual refetch available via context
    refetchInterval: false,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData, // Keep previous data during route transitions to prevent loading flash
  });

  // Start from storage-backed auth, then reconcile auth-relevant employee fields from fresh API data.
  useEffect(() => {
    if (userDetails && authUser && isAuthenticated) {
      const freshCompanyEmployee = userDetails.company_employee || null;

      if (authCompanyEmployeeChanged(authUser.company_employee, freshCompanyEmployee)) {
        console.log('[UserDetailsProvider] company_employee changed, updating auth state:', {
          current: authUser.company_employee,
          new: freshCompanyEmployee,
        });

        updateUser({
          company_employee: freshCompanyEmployee,
        });
      }

      const freshName = [userDetails.first_name, userDetails.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      if (freshName && freshName !== authUser.name) {
        updateUser({ name: freshName });
      }
    }
  }, [userDetails, authUser, isAuthenticated, updateUser]);

  const value: UserDetailsContextValue = {
    userDetails,
    isLoading,
    isError,
    refetch,
  };

  return <UserDetailsContext.Provider value={value}>{children}</UserDetailsContext.Provider>;
}

/**
 * Hook to access user details from React components
 *
 * @throws Error if used outside of UserDetailsProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { userDetails, isLoading, isError } = useUserDetails();
 *
 *   if (isLoading) return <Spinner />;
 *   if (isError || !userDetails) return <div>Error loading user details</div>;
 *
 *   return <div>Welcome, {userDetails.first_name}!</div>;
 * }
 * ```
 */
export function useUserDetails(): UserDetailsContextValue {
  const context = useContext(UserDetailsContext);

  if (!context) {
    throw new Error(
      'useUserDetails must be used within a UserDetailsProvider. ' +
        'Make sure your component tree is wrapped with <UserDetailsProvider apiClient={apiClient}>.'
    );
  }

  return context;
}

/**
 * Convenience hook to get user details without loading states
 * Returns null if loading or error
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const userDetails = useUserDetailsData();
 *
 *   if (!userDetails) return <Spinner />;
 *
 *   return <div>Welcome, {userDetails.first_name}!</div>;
 * }
 * ```
 */
export function useUserDetailsData(): UserDetails | null {
  const { userDetails, isLoading, isError } = useUserDetails();

  if (isLoading || isError || !userDetails) {
    return null;
  }

  return userDetails;
}

export interface TrialStatus {
  /** True while the company is a server-restricted trial org. */
  isTrial: boolean;
  /** When the trial expires (ISO), or null. */
  trialEndTs: string | null;
  /** True once a trial has passed its expiry — the app should lock to the upgrade screen. */
  isExpired: boolean;
}

/**
 * Trial status for the current company, derived from `/users/details`.
 * Drives client-side trial affordances (disabled exports, the trial banner).
 * The server still enforces every restriction — this is UX only.
 */
export function useTrialStatus(): TrialStatus {
  const userDetails = useUserDetailsData();

  // `/users/details` returns the company nested under `company_employee` (there
  // is no top-level company on this endpoint); fall back to a top-level
  // `company` if a future shape provides one.
  const employeeCompany = (
    userDetails?.company_employee as
      | { company?: { account_type?: 'full' | 'trial'; trial_end_ts?: string | null } }
      | null
      | undefined
  )?.company;
  const company = userDetails?.company ?? employeeCompany ?? null;

  // Server-sourced kill switch: when off, no org is a trial on the FE either,
  // so the pill, lockout, and export disables stay inert in lockstep with the
  // backend enforcement. Defaults to enabled if the field is absent.
  const trialAccountsEnabled =
    (userDetails as { trial_accounts_enabled?: boolean })?.trial_accounts_enabled ?? true;
  const isTrial = trialAccountsEnabled && company?.account_type === 'trial';
  const trialEndTs = company?.trial_end_ts ?? null;
  const isExpired = isTrial && trialEndTs != null && new Date(trialEndTs).getTime() <= Date.now();

  return { isTrial, trialEndTs, isExpired };
}

/** Address trials contact to upgrade (conversion is AE-manual; no self-serve checkout yet). */
export const UPGRADE_CONTACT_EMAIL = 'success@commanderai.com';

/**
 * Start the trial-upgrade flow. Today that's a prefilled email to the success
 * team (AE-manual conversion). Centralized so the upgrade action — and any
 * future analytics / HubSpot intent tracking — lives in one place.
 */
export function openTrialUpgrade(): void {
  const subject = encodeURIComponent('Upgrade my Commander trial');
  const body = encodeURIComponent("I'd like to upgrade my Commander trial to a full account.");
  window.open(`mailto:${UPGRADE_CONTACT_EMAIL}?subject=${subject}&body=${body}`, '_self');
}
